import { GridApi } from '@amzn/gft-ag-grid-enterprise';
import { useRef } from 'react';

/**
 * Hook to manage Page Grids for synchronization and operations across different Sections
 */
const usePageGridManager = () => {
  const gridApiRefs = useRef<Map<string, GridApi | null>>(new Map());
  // To track drag reorder state across sections
  const isDragging = useRef(false);

  /**
   * Register a Page Grid for synchronization and operations
   * @param sectionKey - The key of the Section to register the Grid for
   * @param gridApi - The GridApi reference to register
   */
  const registerPageGrid = (sectionKey: string, gridApi: GridApi) => {
    gridApiRefs.current.set(sectionKey, gridApi);
  };

  /**
   * Unregister a Page Grid
   * @param sectionKey - The key of the Section to unregister the Grid for
   */
  const unregisterPageGrid = (sectionKey: string) => {
    gridApiRefs.current.delete(sectionKey);
  };

  /**
   * Set the dragging state when reordering is initiated
   * @param draggingState - The dragging state to set
   */
  const setDragging = (draggingState: boolean) => {
    isDragging.current = draggingState;
  };

  /**
   * Execute an operation on all registered Page Grids
   * @param apiOperation - The operation to execute on each GridApi
   */
  const executeOnAll = (apiOperation: (gridApi: GridApi, index: number, gridCount: number) => void) => {
    // Skip updates when dragging is in progress
    if (isDragging.current) {
      return;
    }

    const gridApis = Array.from(gridApiRefs.current.entries())
      .sort(([a], [b]) => a.localeCompare(b))
      .map(([, api]) => api)
      .filter(Boolean) as GridApi[];
    gridApis.forEach((gridApi, idx) => {
      apiOperation(gridApi, idx, gridApis.length);
    });
  };

  return {
    registerPageGrid,
    unregisterPageGrid,
    isDragging,
    setDragging,
    executeOnAll,
  };
};

export type GridManager = ReturnType<typeof usePageGridManager>;

export default usePageGridManager;
